import * as React from 'react';
import { ValidationProps } from 'ui';
import { actionCreators } from './actions';
import * as ReactDOM from 'react-dom';
import { FIELD_TYPE } from './field-types';
import { SimpleFormComponentProps, SimpleFormContainerProps, SimpleFormMethods, TextLabel, ApiToUse, SimpleFieldFactoryParameters }
  from 'app/infrastructure/simple-form/types';
import {
  textareaCreator, selectSingleCreator, selectSingleHierarchyCreator,
  selectMultipleCreator, selectMultipleHierarchyCreator, dateTimeCreator,
  passwordCreator, checkboxListCreator, checkboxCreator
} from './common';
import { LoadOptions } from 'app/types-shared';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { decimalNumberCreator } from 'app/infrastructure/simple-form/common/decimal-number';
import { deepEquals } from 'app/infrastructure/js';
import get from 'lodash/get';

/**
 * In order to use that component:
 *
 * 1. Add following lines in your imports:
 *
 * > import { FIELD_TYPE, simpleFieldForPath } from 'app/infrastructure/simple-form/'
 * import { LoadOptions } from 'app/types';
 * import { selectMultipleHierarchyCreator } from './common/selectMultipleHierarchyCreator';
 * import { selectMultipleCreator } from './common/selectMultipleCreator';
 * > const SimpleForm = simpleFieldForPath(STATE.<<YOUR_PATH_IN_STORE>>);
 *
 * 2. Configure your reducer to use its own reducers and generated from factory for this component:
 *
 * > export const reducer: Reducer<ProfileState> = (state: ProfileState = INITIAL_PROFILE_STATE, action: any) => {
 * > const r = reducers[action.type];
 * > return (r && r(state, action)) || simpleFormReducer(<<YOUR_PATH_IN_STORE>>)(state, action); };
 *
 * 3. Add SimpleForm to your component:
 *
 * > <SimpleForm
 * >  name="name"
 * >  type={FIELD_TYPE.TEXTAREA}
 * >  label={{ text: 'Your name', label: 'user' }} />
 *
 * Description of SimpleForm API is in 'types.ts' SimpleFormComponentData.
 *
 */

// tslint:disable:max-func-body-length
export const simpleFieldForPath = (path: string | string[], parameters: SimpleFieldFactoryParameters = {}):
  React.ComponentType<SimpleFormComponentProps> => {

  const actions = actionCreators(path);

  class SimpleField extends React.Component<SimpleFormContainerProps & typeof actions, any> {
    public static statePath: string | string[] = path;
    public state: Readonly<any> = {
      loadedOptions: {},
      loadedCategoryOptions: {}
    };

    private refsToScrollableComponent: any = {};

    private getProperty = (name: string) => {
      const value = get(this.props, `simpleForm.editing.values[${name}].value`);
      return !!value || value === 0 ? value : undefined;
    }

    private getOptions = (name: string) => {
      return this.props.simpleForm?.tempdata?.options?.[name]?.data;
    }

    private getCategoryOptions = (name: string) => {
      return this.props.simpleForm?.tempdata?.categoryOptions?.[name]?.data;
    }

    private getValidation = (name: string): ValidationProps => {
      const externalValidation = this.props.serverValidation || get(this.props, `validation[${name}]`);
      const simpleFormValidation = get(this.props, `simpleForm.editing.validation[${name}]`);
      return {
        level: Math.max(externalValidation?.level || 0, simpleFormValidation?.level || 0),
        messages: [
          ...(externalValidation?.messages || []),
          ...(simpleFormValidation?.messages || [])
        ],
        enableMultilineValidation: simpleFormValidation?.enableMultilineValidation
      };
    }

    private onLoadOptions = (refName: string, optionsEndpoint: string, optionsParams: any) => (event: LoadOptions) => {
      this.props.loadOptions(
        refName,
        optionsEndpoint,
        event,
        { ...optionsParams, displayNotSelected: parameters.displayNotSelectedOption },
        this.props.apiToUse || ApiToUse.API
      );
      this.setState({ loadedOptions: { [refName]: event } });
      return;
    }

    private onLoadCategoryOptions = (
      refName: string,
      categoryOptionsEndpoint: string,
      categoryOptionsParams: any) =>
        (event: LoadOptions) => {
        this.props.loadCategoryOptions(
          refName,
          categoryOptionsEndpoint,
          event,
          { ...categoryOptionsParams },
          this.props.apiToUse || ApiToUse.API
        );
        this.setState({ loadedCategoryOptions: { [refName]: event } });
        return;
    }

    private handleChange = (refName: string) => (event: any) => {
      this.props.valueChanged(refName, event);
      this.props.clearServerValidation && this.props.clearServerValidation(event);
      
      //force clearing code values in the store when all code values removed;
      //this is done to reinforce validation on clearing code values in the component
      if(Array.isArray(event) && !event?.length) {
        this.props.clearValueInStore(refName);
      }
    }

    private handleBlur = (refName: string) => (event: any) => {
      if (this.props.trimOnBlur) {
        (typeof event === 'string' || event instanceof String) && this.props.valueChanged(refName, event.trim());
      }
    }

    private isFetching = (refName: string) => {
      const tempdata = this.props.simpleForm?.tempdata;
      const isFetching = tempdata?.options?.[refName]?.fetching || tempdata?.categoryOptions?.[refName]?.fetching;
      const isFocusedAndFetchingDidntStartYet = !tempdata.options?.[refName];

      return isFetching || isFocusedAndFetchingDidntStartYet;
    }

    private createRefToScrollableComponent = (refName: string) => (target) => {
      this.refsToScrollableComponent[refName] = ReactDOM.findDOMNode(target);
    }

    private buildLabel = (label: TextLabel) => {
      return label && {
        text: label.text,
        visible: true,
        icon: label.label || null,
        required: label.required || false
      };
    }

    private fieldMethods: SimpleFormMethods = {
      getProperty: this.getProperty,
      getOptions: this.getOptions,
      getCategoryOptions: this.getCategoryOptions,
      getValidation: this.getValidation,
      onLoadOptions: this.onLoadOptions,
      onLoadCategoryOptions: this.onLoadCategoryOptions,
      handleChange: this.handleChange,
      isFetching: this.isFetching,
      createRefToScrollableComponent: this.createRefToScrollableComponent,
      buildLabel: this.buildLabel,
      handleBlur: this.handleBlur
    };

    private loadOptionsForEndpoint = () => {
      const { name, initialValue } = this.props;

      let loadOptions = null;

      if (initialValue && initialValue.value !== 0) {
        loadOptions = { siblingId: initialValue.value, displayNotSelected: parameters.displayNotSelectedOption } as LoadOptions;
        this.setState({ loadedOptions: { [name]: loadOptions } });
      }

      const { optionsEndpoint, optionsParams } = this.props;

      this.refsToScrollableComponent[name].scrollIntoView();

      this.props.loadOptions(name, optionsEndpoint, loadOptions, optionsParams);
    }

    public componentDidUpdate(prevProps: SimpleFormContainerProps) {
      const { serialize, validationProps, name, simpleForm } = this.props;

      if (prevProps.hasFocus !== this.props.hasFocus) {
        this.loadOptionsForEndpoint();
      }

      const componentData = simpleForm && simpleForm.componentData && simpleForm.componentData[name];
      const prevSerialize = componentData && componentData.serialize;
      const prevValidation = componentData && componentData.validationProps;

      if (!deepEquals(prevSerialize, serialize) || !deepEquals(prevValidation, validationProps)) {
        this.updateComponent();
      }
    }

    public componentDidMount() {
      const { hasFocus, initialValue, name, insertValueToStore } = this.props;

      if (hasFocus) {
        this.loadOptionsForEndpoint();
      }
      if (initialValue && initialValue.value === 0) {
        insertValueToStore({
          [name]: {
            value: { value: 0, description: '' }
          }
        });
      } else {
        insertValueToStore({ [name]: { value: initialValue } });
      }
      this.updateComponent();
    }

    public componentWillUnmount() {
      const { name, clearValueInStore } = this.props;
      clearValueInStore(name);
    }

    private updateComponent = () => {
      const { name, type, serialize, validationProps, insertComponentDataToStore } = this.props;

      if (type && name) {
        insertComponentDataToStore(name, type, serialize, validationProps);
      }
    }

    private buildField = () => {
      const typeMaps = {
        [FIELD_TYPE.TEXTAREA]: textareaCreator,
        [FIELD_TYPE.SELECT_SINGLE_HIERARCHY]: selectSingleHierarchyCreator,
        [FIELD_TYPE.SELECT_SINGLE]: selectSingleCreator,
        [FIELD_TYPE.CHECKBOX_LIST]: checkboxListCreator,
        [FIELD_TYPE.CHECKBOX]: checkboxCreator,
        [FIELD_TYPE.SELECT_MULTIPLE]: selectMultipleCreator,
        [FIELD_TYPE.SELECT_MULTIPLE_HIERARCHY]: selectMultipleHierarchyCreator,
        [FIELD_TYPE.DATE_TIME]: dateTimeCreator,
        [FIELD_TYPE.PASSWORD]: passwordCreator,
        [FIELD_TYPE.DECIMAL_NUMBER]: decimalNumberCreator
      };
      return this.props.type && this.props.name && typeMaps[this.props.type]
        && typeMaps[this.props.type](this.props, this.fieldMethods, this.state) || null;
    }

    public render() {
      return this.buildField();
    }
  }

  return connect<any, any, SimpleFormComponentProps>(
    (state: any, props: any) => ({ ...get(state, path), ...{ locale: state.app.locale } }),
    dispatch => bindActionCreators(actions, dispatch)
  )(SimpleField);

};
